import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import RightTimeApi from "@/API";
import Modal from "@modalSrc/Modal";

type SectionPickerModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (section: any) => Promise<boolean | void> | boolean | void;
  excludedSectionIds?: Array<number | string>;
  updateSpinner?: ((isLoading: boolean) => void) | null;
  title?: string;
};

export default function SectionPickerModal({
  isOpen,
  onClose,
  onSelect,
  excludedSectionIds = [],
  updateSpinner = null,
  title = "Select Section",
}: SectionPickerModalProps) {
  const [sections, setSections] = useState<any[]>([]);
  const [selectedSectionId, setSelectedSectionId] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const excludedIds = useMemo(
    () => new Set((excludedSectionIds || []).map((id) => Number(id)).filter((id) => id > 0)),
    [excludedSectionIds]
  );

  const availableSections = useMemo(() => {
    return sections
      .filter((section) => {
        const sectionId = Number(section?.sectionId || section?.id || 0);
        return sectionId > 0 && !excludedIds.has(sectionId);
      })
      .sort((a, b) => {
        const left = String(a?.name || a?.Name || "");
        const right = String(b?.name || b?.Name || "");
        return left.localeCompare(right);
      });
  }, [sections, excludedIds]);

  useEffect(() => {
    if (!isOpen) {
      setSections([]);
      setSelectedSectionId("");
      setIsLoading(false);
      setIsSubmitting(false);
      return;
    }

    loadSectionsAsync();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    if (availableSections.length <= 0) {
      setSelectedSectionId("");
      return;
    }

    const selectedId = Number(selectedSectionId || 0);
    const selectedExists = availableSections.some((section) => Number(section?.sectionId || section?.id || 0) === selectedId);
    if (!selectedExists) {
      setSelectedSectionId(String(availableSections[0]?.sectionId || availableSections[0]?.id || ""));
    }
  }, [availableSections, isOpen, selectedSectionId]);

  async function loadSectionsAsync() {
    setIsLoading(true);
    if (typeof updateSpinner === "function") updateSpinner(true);

    try {
      const resp = await RightTimeApi.getAsync("/Sections");
      if (!resp?.ok) {
        toast.error("Failed to load sections.");
        setSections([]);
        return;
      }

      const data = resp?.jsonData ?? resp?.data ?? [];
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.sections)
          ? data.sections
          : Array.isArray(data?.Sections)
            ? data.Sections
            : [];

      setSections(list);
    } catch {
      toast.error("Error loading sections.");
      setSections([]);
    } finally {
      setIsLoading(false);
      if (typeof updateSpinner === "function") updateSpinner(false);
    }
  }

  async function handleSelectClick() {
    if (!selectedSectionId) return;

    const selectedId = Number(selectedSectionId);
    const selectedSection = availableSections.find(
      (section) => Number(section?.sectionId || section?.id || 0) === selectedId
    );

    if (!selectedSection) return;

    setIsSubmitting(true);
    try {
      const result = await onSelect?.(selectedSection);
      if (result !== false) {
        onClose();
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="sm"
      footer={(
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSelectClick}
            disabled={isLoading || isSubmitting || !selectedSectionId}
          >
            {isSubmitting ? "Selecting..." : "Select"}
          </button>
        </>
      )}
    >
      {isLoading ? (
        <p>Loading sections...</p>
      ) : availableSections.length > 0 ? (
        <>
          <label htmlFor="section-picker-select">Section</label>
          <select
            id="section-picker-select"
            title="Select section"
            value={selectedSectionId}
            onChange={(e) => setSelectedSectionId(e.target.value)}
          >
            {availableSections.map((section) => {
              const sectionId = Number(section?.sectionId || section?.id || 0);
              const sectionName = section?.name || section?.Name || `Section ${sectionId}`;
              return (
                <option key={sectionId} value={sectionId}>
                  {sectionName}
                </option>
              );
            })}
          </select>
        </>
      ) : (
        <p>No additional sections are available to add.</p>
      )}
    </Modal>
  );
}
